import {
  GlobeAltIcon,
  TicketIcon,
} from '@heroicons/react/24/outline'
import { useId, useState } from 'react'

export type ChatLinkIconKind = 'internal' | 'jira' | 'netsuite' | 'salesforce' | 'external'

const iconClass =
  'inline-block h-3.5 w-3.5 shrink-0 bg-transparent no-underline'
const faviconClass = `${iconClass} object-contain rounded-[2px]`
/** Centers the glyph on the first text line when the label wraps. */
const firstLineIconSlotClass = 'mr-1 inline-flex h-[1lh] shrink-0 items-center'

const BRAND_FALLBACK_ICONS: Record<'jira' | 'netsuite' | 'salesforce', string> = {
  jira: '/chat-icon-jira.png',
  netsuite: '/chat-icon-netsuite.png',
  salesforce: '/chat-icon-salesforce.png',
}

/** Map a URL to a leading icon kind (known vendors vs same-origin admin vs other external). */
export function resolveChatLinkIconKind(
  href: string | undefined | null,
  origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
): ChatLinkIconKind {
  if (!href?.trim()) return 'internal'
  const trimmed = href.trim()
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return 'internal'
  }
  try {
    const url = new URL(trimmed, origin)
    if (url.origin === new URL(origin).origin) return 'internal'
    const host = url.hostname.toLowerCase()
    if (host.includes('atlassian.net') || host.includes('jira')) return 'jira'
    if (host.includes('netsuite.com') || host.includes('oraclecloud.com')) return 'netsuite'
    if (host.includes('salesforce.com') || host.includes('force.com') || host.includes('lightning.force')) {
      return 'salesforce'
    }
    return 'external'
  } catch {
    return 'external'
  }
}

/** Hostname used for favicon lookup, or null for same-origin / relative links. */
export function hostnameForFavicon(
  href: string | undefined | null,
  origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
): string | null {
  if (!href?.trim()) return null
  const trimmed = href.trim()
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return null
  }
  try {
    const url = new URL(trimmed, origin)
    if (url.origin === new URL(origin).origin) return null
    return url.hostname || null
  } catch {
    return null
  }
}

/**
 * External favicon URL for a link (Google favicon service).
 * Returns null for internal links and known brand hosts (those use local icons).
 */
export function faviconUrlForHref(
  href: string | undefined | null,
  origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
): string | null {
  const host = hostnameForFavicon(href, origin)
  if (!host) return null
  const kind = resolveChatLinkIconKind(href, origin)
  // Prefer local brand PNGs — Google often returns a generic “fallback” favicon that still 200s.
  if (kind === 'jira' || kind === 'netsuite' || kind === 'salesforce') return null
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`
}

/** Local brand PNG for Jira / NetSuite / Salesforce (preferred over remote favicons). */
export function brandFallbackIconForHref(
  href: string | undefined | null,
  origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
): string | null {
  const kind = resolveChatLinkIconKind(href, origin)
  if (kind === 'jira' || kind === 'netsuite' || kind === 'salesforce') {
    return BRAND_FALLBACK_ICONS[kind]
  }
  return null
}

type ChatLinkIconProps = {
  /** Explicit kind; when omitted, derived from `href` when provided. */
  kind?: ChatLinkIconKind
  href?: string | null
}

/** King mark in brand red for same-origin / relative admin links. */
const InternalLinkIcon = () => {
  const maskId = `king-chat-icon-mask-${useId().replace(/:/g, '')}`
  return (
    <span className={firstLineIconSlotClass} aria-hidden>
      <svg
        viewBox="0 0 242 220"
        width={14}
        height={14}
        preserveAspectRatio="xMidYMid meet"
        className={iconClass}
      >
        <defs>
          <path
            id={`${maskId}-path`}
            d="M40.441,0 L62.366,27.5 L79.13,27.5 L101.054,0 L140.099,0 L162.024,27.5 L178.788,27.5 L200.713,0 L241.154,0 L241.154,220 L0,220 L0,0 L40.441,0 Z M224.231,80.385 L16.923,80.385 L16.923,203.077 L224.231,203.077 L224.231,80.385 Z M105.769,99.423 L105.769,114.231 L80.385,114.231 L80.385,144.603 L145.517,114.231 L120.577,114.231 L120.577,99.423 L205.192,99.423 L205.192,114.231 L175.552,114.231 L136.736,132.33 L179.188,169.231 L205.192,169.231 L205.192,184.038 L120.577,184.038 L120.577,169.231 L150.169,169.231 L117.85,141.136 L80.385,158.605 L80.385,169.231 L105.769,169.231 L105.769,184.038 L35.962,184.038 L35.962,169.231 L61.346,169.231 L61.346,114.231 L35.962,114.231 L35.962,99.423 L105.769,99.423 Z M32.29,16.923 L16.923,16.923 L16.923,63.461 L224.231,63.461 L224.231,16.923 L208.864,16.923 L186.939,44.423 L153.873,44.423 L131.948,16.923 L109.205,16.923 L87.281,44.423 L54.215,44.423 L32.29,16.923 Z"
          />
          <mask id={maskId} fill="white">
            <use href={`#${maskId}-path`} />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="242"
          height="220"
          fill="#ef3e3e"
          mask={`url(#${maskId})`}
        />
      </svg>
    </span>
  )
}

type ExternalFaviconProps = {
  faviconUrl: string
  kind: ChatLinkIconKind
}

const ExternalFavicon: React.FC<ExternalFaviconProps> = ({ faviconUrl, kind }) => {
  const [failed, setFailed] = useState(false)

  if (!failed) {
    return (
      <span className={firstLineIconSlotClass} aria-hidden>
        <img
          src={faviconUrl}
          alt=""
          className={faviconClass}
          width={14}
          height={14}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      </span>
    )
  }

  const Icon = kind === 'jira' ? TicketIcon : GlobeAltIcon
  return (
    <span className={firstLineIconSlotClass} aria-hidden>
      <Icon className={iconClass} />
    </span>
  )
}

type BrandIconProps = {
  src: string
  kind: ChatLinkIconKind
}

const BrandIcon: React.FC<BrandIconProps> = ({ src, kind }) => {
  const [failed, setFailed] = useState(false)

  if (!failed) {
    return (
      <span className={firstLineIconSlotClass} aria-hidden>
        <img
          src={src}
          alt=""
          className={faviconClass}
          width={14}
          height={14}
          decoding="async"
          onError={() => setFailed(true)}
        />
      </span>
    )
  }

  const Icon = kind === 'jira' ? TicketIcon : GlobeAltIcon
  return (
    <span className={firstLineIconSlotClass} aria-hidden>
      <Icon className={iconClass} />
    </span>
  )
}

/** Leading affordance on Sage chat links — brand icons for known vendors, else remote favicon. */
export const ChatLinkIcon: React.FC<ChatLinkIconProps> = ({ kind, href }) => {
  const resolved = kind ?? resolveChatLinkIconKind(href)
  const faviconUrl = faviconUrlForHref(href)
  const brandIcon = brandFallbackIconForHref(href)

  if (resolved === 'internal') {
    return <InternalLinkIcon />
  }

  if (brandIcon) {
    return <BrandIcon src={brandIcon} kind={resolved} />
  }

  if (faviconUrl) {
    return <ExternalFavicon faviconUrl={faviconUrl} kind={resolved} />
  }

  const Icon = resolved === 'jira' ? TicketIcon : GlobeAltIcon
  return (
    <span className={firstLineIconSlotClass} aria-hidden>
      <Icon className={iconClass} />
    </span>
  )
}
